<script lang="ts">
  import 'focus-visible';
  import { setContext, onMount } from 'svelte';
  import { DefaultTwistTheme, TwistThemeProvider } from '@twist/core';
  import { enableVisualEditing, type HistoryAdapterNavigate } from '@sanity/visual-editing';
  import { isPreviewing, useLiveMode } from '@sanity/svelte-loader';
  import { page } from '$app/stores';
  import { newSanityContext, key } from '$lib/context/sanity';
  import { flagsContextKey, flagValue } from '$lib/context/flags';
  import { FEATURE_FLAGS } from '$lib/flags/constants';
  import { pageHydrated } from '$lib/stores/loadStatus';
  import { client, sanityStudioURL } from '$lib/client';

  import { afterNavigate, goto, invalidateAll } from '$app/navigation';
  import BaseLayout from '$components/BaseLayout.svelte';
  import Meta from '$components/Head/Meta.svelte';
  import PresentationPreviewControls from '$components/PresentationPreviewControls.svelte';
  import type { VisualEditingProps } from '@sanity/visual-editing/svelte';

  let navigate: HistoryAdapterNavigate | undefined;
  let innerHeight: number;

  export let zIndex: VisualEditingProps['zIndex'] = undefined;
  export let refresh: VisualEditingProps['refresh'] = undefined;

  let navigatingFromUpdate = false;

  setContext(flagsContextKey, $page.data.flags);
  setContext(key, newSanityContext());

  function onLoad() {
    $pageHydrated = true;
  }

  onMount(() =>
    enableVisualEditing({
      zIndex,
      refresh: (payload) => {
        // Handles preview refresh logic, so page is properly refreshed in presentation mode
        function refreshDefault() {
          if (payload.source === 'mutation' && payload.livePreviewEnabled) {
            return false;
          }
          return new Promise<void>((resolve) => {
            invalidateAll();
            resolve();
          });
        }

        return refresh ? refresh(payload, refreshDefault) : refreshDefault();
      },
      history: {
        subscribe: (_navigate) => {
          // Sets up navigation and sync presentation mode editor with current url
          navigate = _navigate;
          navigate({
            type: 'replace',
            url: window.location.pathname + window.location.search
          });

          return () => {
            navigate = undefined;
          };
        },
        update: (update) => {
          // Applies navigation updates from the presentation mode editor (push, replace, pop)
          if (update.type === 'push' || update.type === 'replace') {
            navigatingFromUpdate = true;
            goto(update.url, { replaceState: update.type === 'replace' });
          } else if (update.type === 'pop') {
            history.back();
          }
        }
      }
    })
  );

  onMount(() => {
    // if document became ready before this component mounted
    if (document.readyState !== 'loading') {
      $pageHydrated = true;
    }

    useLiveMode({
      studioUrl: sanityStudioURL,
      client: client.withConfig({
        stega: { ...client.config().stega, enabled: false }
      })
    });
  });

  afterNavigate(async ({ to, complete }) => {
    if (navigate && to && !navigatingFromUpdate) {
      await complete;
      navigate({ type: 'push', url: to.url.pathname + to.url.search });
    }
    navigatingFromUpdate = false;
  });

  $: brandReskinEnabled = flagValue(FEATURE_FLAGS.BRAND_RESKIN);

  $: if ($pageHydrated) {
    document.body.style.setProperty('--vh', `${innerHeight}px`);

    if (brandReskinEnabled) {
      document.body.classList.add('reskin');
    }
  }

  $: seoAttributes = $page.data?.seoAttributes;

  $: customTheme = {
    ...DefaultTwistTheme,
    tokens: {
      ...DefaultTwistTheme.tokens,
      fonts: {
        ...DefaultTwistTheme.tokens.fonts,
        'font-family-primary': brandReskinEnabled
          ? 'var(--twist-font-family-cash-sans)'
          : 'var(--twist-font-family-pilat-wide)',
        'font-family-secondary': brandReskinEnabled
          ? 'var(--twist-font-family-cash-sans)'
          : 'var(--twist-font-family-pp-neue-machina)'
      }
    }
  };
</script>

<svelte:window on:load={onLoad} bind:innerHeight />

{#if seoAttributes}
  <Meta {seoAttributes} />
{/if}

<TwistThemeProvider theme={customTheme}>
  <BaseLayout>
    <slot />
  </BaseLayout>
</TwistThemeProvider>

{#if $isPreviewing}
  <PresentationPreviewControls />
{/if}

<style global lang="scss">
  @import '@twist/ui/dist/styles/main.scss';
  @import 'src/scss/media';
  @import 'src/scss/variables';

  body {
    overflow-x: hidden;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
  }

  :global(:root) {
    --bgColor: #fff;
  }

  :global(:root[reduce-color='true']) {
    --bgColor: #efefef;
  }

  :global(body) {
    background: var(--bgColor);
  }

  :global(body.reskin) {
    --bgColor: #fbfbfb;
  }

  html.modal-open,
  html.modal-open body {
    overflow: hidden;
  }
</style>
